import dayjs from "dayjs";

export function assignZIndexes(
  appointments: {
    startTime: string;
    overlapIndex: number | null;
    duration: number;
    endTime: string;
    appointmentId: number;
    date: string;
    isOnPreviousResource: boolean;
    timeOffStart?: number | null;
    timeOffDuration?: number | null;
    timeOffEnd?: number | null;
    formattedData: {
      startTime: string;
    };
    appointmentStart?: Date;
    appointmentEndWithoutTimeOff?: Date;
    appointmentBlockingIntervals?: {
      start: Date;
      end: Date;
    }[];
    actualBlockingDuration?: number;
    // durationZIndex?: number;
  }[],
  timeStep: number,
) {
  // How much shared time two appointments need before we force side-by-side layout.
  const sideBySideThreshold = timeStep / 2;
  const sorted = structuredClone(appointments).map((a) => ({
    ...a,
    overlapIndex: null as number | null,
    overlapCount: 1,
    // Shorter appointments get higher z-index so they're always visible on top
    // durationZIndex: Math.max(0, 1000 - Math.min(a.duration, 999)),
  }));

  for (const appointment of sorted) {
    // Precompute date boundaries once so we don't repeat date math in overlap checks.
    const appointmentStart = new Date(
      `${appointment.date}T${appointment.startTime}`,
    );
    const appointmentEnd = new Date(
      appointmentStart.getTime() + appointment.duration * 60000,
    );
    const timeOffStartTime = new Date(
      appointmentStart.getTime() +
        (appointment.timeOffStart ?? appointment.duration) * 60000,
    );
    appointment.appointmentStart = appointmentStart;
    appointment.appointmentEndWithoutTimeOff = timeOffStartTime;
    // Represents the actual "blocking" pieces of this appointment.
    // If appointment has timeoff in the middle, this becomes two intervals:
    // [start -> timeoffStart] and [timeoffEnd -> end].
    appointment.appointmentBlockingIntervals = getAppointmentBlockingIntervals({
      appointmentStart,
      appointmentEnd,
      timeOffStart: appointment.timeOffStart,
      timeOffDuration: appointment.timeOffDuration,
    });
    appointment.actualBlockingDuration = getTotalBlockingDurationMinutes(
      appointment.appointmentBlockingIntervals,
    );
  }

  // Sort by start time, then by duration descending
  // Longer appointments get processed first (lower overlapIndex = behind)
  // Shorter appointments get processed later (higher overlapIndex = on top)
  sorted.sort((a, b) => {
    const timeDiff =
      a.appointmentStart!.getTime() - b.appointmentStart!.getTime();
    if (timeDiff !== 0) return timeDiff;
    // Same start time: longer appointments first (get lower overlapIndex)
    return b.duration - a.duration;
  });

  let active: typeof sorted = [];
  for (const appointment of sorted) {
    // Blocking intervals of appointment currently being placed.
    const currentIntervals = appointment.appointmentBlockingIntervals ?? [];

    active = active.filter((a) => {
      const previousIntervals = a.appointmentBlockingIntervals;
      if (!previousIntervals || currentIntervals.length === 0) {
        return false;
      }

      const overlapDuration = getMaxOverlapDurationMinutes(
        previousIntervals,
        currentIntervals,
      );
      // Keep previously active appointments only if they still overlap enough.
      // This controls when blocks should sit side-by-side vs reuse same column.
      // Use actual blocking duration (not total appointment duration) to properly
      // handle appointments with timeoff in the middle.
      const currentBlockingDuration = appointment.actualBlockingDuration ?? 0;
      const previousBlockingDuration = a.actualBlockingDuration ?? 0;
      const dynamicThreshold = Math.min(
        sideBySideThreshold,
        Math.min(currentBlockingDuration, previousBlockingDuration) / 2,
      );
      return overlapDuration > dynamicThreshold;
    });

    const usedArray = active
      .map((a) => a.overlapIndex)
      .filter((x): x is number => x !== null);
    const used = new Set<number>(usedArray);

    let index = 0;
    while (used.has(index)) {
      index++;
    }

    // First free overlap slot (0,1,2...) decides x offset in the UI.
    appointment.overlapIndex = index;
    active.push(appointment);

    // Every appointment in current active group needs the same overlapCount baseline.
    const currentCount = active.length;
    for (const a of active) {
      a.overlapCount = Math.max(a.overlapCount, currentCount);
    }
  }

  return sorted;
}

function getAppointmentBlockingIntervals({
  appointmentStart,
  appointmentEnd,
  timeOffStart,
  timeOffDuration,
}: {
  appointmentStart: Date;
  appointmentEnd: Date;
  timeOffStart?: number | null;
  timeOffDuration?: number | null;
}) {
  // No valid timeoff => whole appointment is one blocking interval.
  if (
    timeOffStart == null ||
    timeOffDuration == null ||
    timeOffDuration <= 0 ||
    timeOffStart >=
      Math.round(
        (appointmentEnd.getTime() - appointmentStart.getTime()) / 60000,
      )
  ) {
    return [{ start: appointmentStart, end: appointmentEnd }];
  }

  const timeOffStartTime = new Date(
    appointmentStart.getTime() + timeOffStart * 60000,
  );
  const timeOffEndTime = new Date(
    timeOffStartTime.getTime() + timeOffDuration * 60000,
  );

  const intervals: { start: Date; end: Date }[] = [];

  // Part before timeoff.
  if (timeOffStartTime.getTime() > appointmentStart.getTime()) {
    intervals.push({ start: appointmentStart, end: timeOffStartTime });
  }

  // Part after timeoff.
  if (appointmentEnd.getTime() > timeOffEndTime.getTime()) {
    intervals.push({ start: timeOffEndTime, end: appointmentEnd });
  }

  // Safety fallback for any edge case where split produced no usable intervals.
  return intervals.length > 0
    ? intervals
    : [{ start: appointmentStart, end: appointmentEnd }];
}

function getMaxOverlapDurationMinutes(
  firstIntervals: { start: Date; end: Date }[],
  secondIntervals: { start: Date; end: Date }[],
) {
  let maxOverlap = 0;

  // Compare every interval pair and keep the biggest overlap in minutes.
  // Example: if two appointments overlap in both "before" and "after" segments,
  // we use the larger overlap chunk for threshold comparison.
  for (const firstInterval of firstIntervals) {
    for (const secondInterval of secondIntervals) {
      const overlapStart = Math.max(
        firstInterval.start.getTime(),
        secondInterval.start.getTime(),
      );
      const overlapEnd = Math.min(
        firstInterval.end.getTime(),
        secondInterval.end.getTime(),
      );
      const overlapInMinutes = (overlapEnd - overlapStart) / 60000;
      if (overlapInMinutes > maxOverlap) {
        maxOverlap = overlapInMinutes;
      }
    }
  }

  return maxOverlap;
}

function getTotalBlockingDurationMinutes(
  intervals: { start: Date; end: Date }[],
): number {
  return intervals.reduce((total, interval) => {
    return total + (interval.end.getTime() - interval.start.getTime()) / 60000;
  }, 0);
}

export const returnYoffsetForEvent = ({
  startTimeEvent,
  calendarStartTime,
  timeStep,
  slotHeight,
}: {
  startTimeEvent: string;
  calendarStartTime: string;
  timeStep: number;
  slotHeight: number;
}) => {
  const minutes = dayjs(dayjs(startTimeEvent).format("HH:mm"), "HH:mm").diff(
    dayjs(calendarStartTime, "HH:mm"),
    "minute",
  );
  return Math.round((minutes / timeStep) * slotHeight);
};

export const returnHeightOfEventBlock = ({
  duration,
  timeStep,
  slotHeight,
}: {
  duration: number;
  timeStep: number;
  slotHeight: number;
}) => {
  const ratio = duration / timeStep;
  return Math.round(ratio * slotHeight);
};
